import apiClient from "./axiosConfig";

export interface ChangeSnapshot<T = Record<string, unknown>> {
  before?: T | null;
  after?: T | null;
}

/** The client a billing audit target (invoice, payment, receipt…) belongs to. */
export interface AuditTargetTenant {
  id: string;
  name: string;
  /** pm2 app name. */
  slug: string | null;
}

export interface AuditLogEntry {
  _id: string;
  /** null for system entries. */
  actorId: string | null;
  actorName?: string;
  /**
   * "system" for work no admin started: a gateway payment arriving, a receipt being numbered, the
   * nightly reconcile. Entries written before 2026-10-29 omit it (all were admins).
   */
  actorType?: "user" | "system";
  /** Where a system entry came from: "webhook", "pay_return", "link_recheck", "nightly_reconcile", "backfill". */
  source?: string | null;
  action: string;
  targetId?: string;
  targetName?: string;
  targetTenant?: AuditTargetTenant | null;
  timestamp: string;
  changes: ChangeSnapshot;
  /** Hash chain (models/AuditLog.js). Empty when the server had no HASH_KEY at write time. */
  hash?: string;
  prevHash?: string;
  hashVersion?: number;
}

export interface AuditLogQuery {
  targetId?: string;
  actorId?: string;
  /** "system": only entries no admin made; "user": only admins' changes. */
  actorType?: "user" | "system";
  action?: string | string[];
  actionPrefix?: string;
  actionContains?: string;
  moduleKey?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
}

export const fetchAuditLogs = async (
  query: AuditLogQuery = {}
): Promise<{ data: AuditLogEntry[]; page: number; pageSize: number; total: number }> => {
  const params = {
    ...query,
    action: Array.isArray(query.action) ? query.action.join(",") : query.action,
  };
  const res = await apiClient.get<
    AuditLogEntry[] | { data: AuditLogEntry[]; page: number; pageSize: number; total: number }
  >("/api/audit-logs", { params });
  if (Array.isArray(res.data)) {
    return {
      data: res.data,
      page: 1,
      pageSize: res.data.length,
      total: res.data.length,
    };
  }
  return res.data;
};

export const exportAuditLogs = async (query: AuditLogQuery = {}): Promise<Blob> => {
  const params = {
    ...query,
    action: Array.isArray(query.action) ? query.action.join(",") : query.action,
  };
  const res = await apiClient.get<Blob>("/api/audit-logs/export", {
    params,
    responseType: "blob",
  });
  return res.data;
};
